import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '~/components/common/pagination';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { formatRelative } from '~/utils/date';

import { useFunnelProducts } from '../../hooks/use-batch-funnel';
import type { FunnelProduct, FunnelProductsQuery } from '../../types/batch-funnel.api.types';

import { FunnelProductTrace } from './funnel-product-trace';
import { EmptyList, FunnelUpstreamProducts, ListSkeleton } from './funnel-upstream-products';

/** What a click on a chip / outcome / issue asks the sheet to list. */
export interface FunnelDrillTarget {
  title: string;
  query: FunnelProductsQuery;
}

interface FunnelProductsSheetProps {
  jobId: string;
  /** `null` keeps the sheet closed. Key the sheet on it so paging resets. */
  target: FunnelDrillTarget | null;
  onClose: () => void;
}

/**
 * "Which products exactly?" side sheet behind every funnel number. Calls the
 * funnel products endpoint, then renders by `source`: inline enriched rows
 * (`workflow`), the upstream curation / delivery listing via `productsPath`,
 * or the server's `unavailableReason`. A product row opens its trace in place.
 */
export function FunnelProductsSheet({ jobId, target, onClose }: FunnelProductsSheetProps) {
  const { t } = useTranslation('catalog-operations');
  const [page, setPage] = useState(0);
  const [traceProductId, setTraceProductId] = useState<string | null>(null);
  const { data, isLoading, isError } = useFunnelProducts(
    jobId,
    target ? { ...target.query, page } : null,
  );

  /** Body by `source`; the trace view replaces the list while open. */
  function renderBody() {
    if (traceProductId) {
      return (
        <FunnelProductTrace
          jobId={jobId}
          productId={traceProductId}
          onBack={() => {
            setTraceProductId(null);
          }}
        />
      );
    }
    if (isLoading) return <ListSkeleton />;
    if (isError || !data) {
      return (
        <p className="text-error text-sm" data-testid="batch-funnel-drill-error">
          {t('batchFunnel.drill.listError')}
        </p>
      );
    }
    if (data.source === 'unavailable') {
      return (
        <p className="text-muted-foreground text-sm" data-testid="batch-funnel-drill-unavailable">
          {data.unavailableReason ?? t('batchFunnel.drill.unavailable')}
        </p>
      );
    }
    if ((data.source === 'curation' || data.source === 'integrations') && data.upstreamRef) {
      return (
        <FunnelUpstreamProducts
          jobId={jobId}
          upstreamRef={data.upstreamRef}
          rejectedReason={data.outcome === 'REJECTED' ? data.reason : null}
          onOpenTrace={setTraceProductId}
        />
      );
    }
    const products = data.products ?? [];
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground text-xs" data-testid="batch-funnel-drill-total">
          {t('batchFunnel.drill.total', { count: data.totalProducts })}
        </p>
        {products.length === 0 ? (
          <EmptyList />
        ) : (
          <WorkflowRows products={products} onOpenTrace={setTraceProductId} />
        )}
        <Pagination
          currentPage={page + 1}
          totalPages={data.totalPages}
          testId="batch-funnel-drill-workflow-pagination"
          onPageChange={(next) => {
            setPage(next - 1);
          }}
        />
      </div>
    );
  }

  return (
    <Sheet
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl lg:max-w-3xl"
        data-testid="batch-funnel-products-sheet"
      >
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle data-testid="batch-funnel-products-sheet-title">
            {target?.title ?? t('batchFunnel.viewProducts')}
          </SheetTitle>
          <SheetDescription>{t('batchFunnel.drill.description')}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{renderBody()}</div>
      </SheetContent>
    </Sheet>
  );
}

/** Enriched-stage rows served inline by workflow-service. */
function WorkflowRows({
  products,
  onOpenTrace,
}: {
  products: FunnelProduct[];
  onOpenTrace: (productId: string) => void;
}) {
  const { t } = useTranslation('catalog-operations');
  return (
    <Table data-testid="batch-funnel-drill-workflow-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('batchFunnel.drill.colProduct')}</TableHead>
          <TableHead>{t('batchFunnel.drill.colReason')}</TableHead>
          <TableHead className="text-right">{t('batchFunnel.drill.colScore')}</TableHead>
          <TableHead className="text-right">{t('batchFunnel.drill.colUpdated')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {products.map((product, index) => {
          const productId = product.productId;
          return (
            <TableRow
              key={productId ?? index}
              data-testid={`batch-funnel-drill-workflow-row-${productId ?? String(index)}`}
              className={productId ? 'cursor-pointer' : undefined}
              {...(productId
                ? {
                    onClick: () => {
                      onOpenTrace(productId);
                    },
                  }
                : {})}
            >
              <TableCell>
                <p className="text-foreground font-medium">{product.title ?? '—'}</p>
                <p className="text-muted-foreground font-mono text-xs break-all">
                  {product.externalId ?? productId ?? '—'}
                </p>
              </TableCell>
              <TableCell>
                <p className="text-foreground">{product.reason ?? product.outcome ?? '—'}</p>
                {product.errorMessage && (
                  <p className="text-muted-foreground text-xs break-words">
                    {product.errorMessage}
                  </p>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground text-right tabular-nums">
                {product.scoreBefore != null ? product.scoreBefore.toFixed(1) : '—'}
                {' → '}
                {product.scoreAfter != null ? product.scoreAfter.toFixed(1) : '—'}
              </TableCell>
              <TableCell className="text-muted-foreground text-right text-xs whitespace-nowrap">
                {product.updatedAt ? formatRelative(product.updatedAt) : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
